import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { useFounderSpots, withFounderStatus } from '../hooks/useFounderSpots';
import { PlanCards } from '../components/PlanCards';
import { BILLING_OPTIONS, plansForPeriod } from '../lib/pricingPlans';
import { 
  Heart, 
  MessageSquare, 
  Shield, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  Smartphone,
  Link2,
  Bell,
  Camera,
  Menu,
  X,
  Play,
  Coffee,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  User,
  Phone,
  Users,
  Loader2,
  Stethoscope,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CheckoutModal from '../components/CheckoutModal';
import { CookiePreferencesLink } from '../components/CookiePreferencesLink';
import { LandingMenu } from '../components/LandingMenu';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo Component - supports white variant for green backgrounds
const Logo = ({ className = "h-12", darkBg = false }) => (
  <img 
    src="/logo-curacheck-new.png"
    alt="CuraCheck" 
    className={`${className} object-contain`}
    style={{ mixBlendMode: darkBg ? 'normal' : 'screen' }}
  />
);

// Sticky Navigation with GREEN background and glassmorphism
const Navbar = ({ onLoginClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="relative top-0 left-0 right-0 z-50 bg-[#4A7C59]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-12 sm:h-14">
          <Logo className="h-8 sm:h-10" darkBg={false} />

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-4">
            <a href="#come-funziona" className="text-white/90 hover:text-white font-medium transition-colors px-4 py-2 rounded-full hover:bg-white/10">
              Come Funziona
            </a>
            <a href="#prezzi" className="text-white/90 hover:text-white font-medium transition-colors px-4 py-2 rounded-full hover:bg-white/10">
              Prezzi
            </a>
            <a href="#faq" className="text-white/90 hover:text-white font-medium transition-colors px-4 py-2 rounded-full hover:bg-white/10">
              FAQ
            </a>
            <a href="/blog" className="text-white/90 hover:text-white font-medium transition-colors px-4 py-2 rounded-full hover:bg-white/10">
              Blog
            </a>
            <Button 
              onClick={onLoginClick}
              className="bg-white hover:bg-gray-100 text-[#4A7C59] px-6 py-2.5 rounded-full font-semibold shadow-md hover:shadow-lg transition-all"
            >
              Area Famiglia
            </Button>
          </div>

          {/* Mobile Menu Button + Login */}
          <div className="md:hidden flex items-center gap-2">
            <Button 
              onClick={onLoginClick}
              variant="outline"
              size="sm"
              className="bg-white hover:bg-gray-100 text-[#4A7C59] px-3 py-1.5 rounded-full font-semibold text-sm border-0"
            >
              Accedi
            </Button>
            <button 
              className="p-2 rounded-xl hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/20 bg-[#3A6345]">
            <div className="flex flex-col gap-2">
              <a href="#come-funziona" className="text-white/90 font-medium py-3 px-4 rounded-xl hover:bg-white/10">Come Funziona</a>
              <a href="#prezzi" className="text-white/90 font-medium py-3 px-4 rounded-xl hover:bg-white/10">Prezzi</a>
              <a href="#faq" className="text-white/90 font-medium py-3 px-4 rounded-xl hover:bg-white/10">FAQ</a>
              <a href="/blog" className="text-white/90 font-medium py-3 px-4 rounded-xl hover:bg-white/10">Blog</a>
              <Button 
                onClick={onLoginClick}
                className="bg-white hover:bg-gray-100 text-[#4A7C59] w-full py-3 rounded-xl font-semibold mt-2"
              >
                Area Famiglia
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose, videoId = "AWjPD7AtBfU" }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Reset loading state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  // Costruisci l'URL YouTube direttamente
  const youtubeUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-[10000] w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-2xl hover:bg-gray-100 transition-colors"
        data-testid="video-modal-close"
      >
        <X className="w-7 h-7 text-black" />
      </button>
      
      {/* Video Container - usando aspect-ratio per affidabilità */}
      <div 
        className="w-full max-w-5xl mx-4 px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="relative w-full bg-black rounded-2xl overflow-hidden shadow-2xl"
          style={{ aspectRatio: '16/9' }}
        >
          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0f0f0f] z-10">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
                <p className="text-white/70 text-sm">Caricamento video...</p>
              </div>
            </div>
          )}
          
          <iframe
            src={youtubeUrl}
            title="CuraCheck Video Demo"
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            onLoad={() => setIsLoading(false)}
            data-testid="video-iframe"
          />
        </div>
        {/* Caption */}
        <p className="text-center text-white/60 text-sm mt-4">
          Clicca fuori dal video o sulla X per chiudere
        </p>
      </div>
    </div>
  );
};

// YouTube Thumbnail con Play Button (lazy load del video)
const YouTubeThumbnail = ({ videoId }) => {
  const [showVideo, setShowVideo] = useState(false);
  
  if (showVideo) {
    return (
      <div className="relative w-full rounded-2xl overflow-hidden shadow-lg" style={{ aspectRatio: '16/9' }}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`}
          title="Cos'è CuraCheck - Video presentazione"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }
  
  return (
    <div 
      className="relative w-full rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
      style={{ aspectRatio: '16/9' }}
      onClick={() => setShowVideo(true)}
    >
      {/* Thumbnail YouTube */}
      <img 
        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
        alt="Video CuraCheck"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={(e) => {
          // Fallback a thumbnail di qualità inferiore se maxres non disponibile
          e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        }}
      />
      {/* Overlay scuro al hover */}
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300" />
      {/* Play Button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform duration-300">
          <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

// Hero Section - VIDEO BACKGROUND con overlay
const HeroSection = ({ onCtaClick }) => {
  const [showVideo, setShowVideo] = useState(false);
  
  return (
  <>
    <VideoModal 
      isOpen={showVideo} 
      onClose={() => setShowVideo(false)}
      videoId="AWjPD7AtBfU"
    />
    
    <section className="relative min-h-screen flex flex-col">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video 
          className="w-full h-full object-cover"
          autoPlay 
          muted 
          loop 
          playsInline
        >
          <source src="https://customer-assets.emergentagent.com/job_home-care-plus-1/artifacts/qhgr93j0_8817769-uhd_3840_2160_25fps.mp4" type="video/mp4" />
        </video>
        {/* Overlay bianco 82% per leggibilità */}
        <div className="absolute inset-0 bg-white/[0.82]" />
      </div>
      
      {/* Navigation integrata */}
      <nav className="relative z-10 flex justify-between items-center p-4 sm:p-6 max-w-7xl mx-auto w-full">
        <img src="/logo-curacheck-new.png" alt="CuraCheck" className="h-10 sm:h-12" />
        <div className="flex gap-3">
          <Link 
            to="/login"
            className="px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg border-2 border-[#3D9A5D] text-[#3D9A5D] font-semibold hover:bg-[#3D9A5D] hover:text-white transition-all text-sm sm:text-base"
          >
            Accedi
          </Link>
          <Button 
            onClick={onCtaClick}
            className="px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg bg-[#3D9A5D] text-white font-semibold hover:bg-[#1B5E3A] transition-all text-sm sm:text-base"
          >
            Prova Gratis
          </Button>
          <LandingMenu />
        </div>
      </nav>
      
      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-6 max-w-4xl mx-auto">
        {/* Logo centrale */}
        <img 
          src="/logo-curacheck-new.png" 
          alt="CuraCheck" 
          className="h-20 sm:h-24 mb-6 animate-fade-in"
        />
        
        {/* Headline */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 sm:mb-6 text-[#111111] animate-fade-in-delay">
          La sua <span className="text-[#3D9A5D]">sicurezza</span>,
          <br />
          la tua <span className="text-[#3D9A5D]">serenità</span>.
        </h1>
        
        {/* Subtitle */}
        <p className="text-base sm:text-lg md:text-xl text-[#444444] font-medium max-w-xl mb-6 sm:mb-8 leading-relaxed animate-fade-in-delay-2">
          Monitora i tuoi cari anziani con un'app semplice e intuitiva. 
          Ricevi notifiche in tempo reale e resta sempre connesso.
        </p>
        
        {/* CTA Button */}
        <Button 
          onClick={onCtaClick}
          className="px-8 py-4 sm:px-10 sm:py-5 rounded-lg bg-[#3D9A5D] text-white font-bold text-base sm:text-lg hover:bg-[#1B5E3A] transition-all hover:scale-105 shadow-lg animate-fade-in-delay-3"
          data-testid="hero-cta"
        >
          Scopri come funziona
          <ArrowRight className="w-5 h-5 ml-2 inline" />
        </Button>
        <p className="text-sm text-[#666666] font-medium mt-3 animate-fade-in-delay-3" data-testid="hero-trial-line">
          7 giorni gratis, senza carta
        </p>
      </div>
      
      {/* Scroll Indicator */}
      <div className="relative z-10 flex flex-col items-center pb-8 animate-fade-in-delay-3">
        <span className="text-xs font-semibold text-[#666666] tracking-widest uppercase mb-2">Scorri</span>
        <div className="w-5 h-5 border-r-2 border-b-2 border-[#3D9A5D] rotate-45 animate-bounce" />
      </div>
    </section>
    
    {/* CSS Animations */}
    <style>{`
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(15px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-fade-in {
        animation: fadeIn 0.8s ease-out forwards;
      }
      .animate-fade-in-delay {
        animation: fadeIn 0.8s ease-out 0.2s forwards;
        opacity: 0;
      }
      .animate-fade-in-delay-2 {
        animation: fadeIn 0.8s ease-out 0.4s forwards;
        opacity: 0;
      }
      .animate-fade-in-delay-3 {
        animation: fadeIn 0.8s ease-out 0.6s forwards;
        opacity: 0;
      }
    `}</style>
  </>
);
};

// Testimonials - VERDE CHIARO (Sezione 2)
const TestimonialsSection = () => {
  return (
    <section className="py-12 px-4 sm:px-6 bg-[#F4F7F5]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-block text-[#4A7C59] font-semibold text-sm uppercase tracking-wider mb-2">
            Testimonianze
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#2C3E50]">
            Le famiglie che ci hanno scelto
          </h2>
        </div>
      </div>

      <div className="-mx-4 sm:-mx-6">
        <TestimonialsCarousel />
      </div>
    </section>
  );
};

// What is CuraCheck - Simple explanation section
const WhatIsCuraCheckSection = () => {
  return (
    <section className="py-16 px-4 sm:px-6 bg-[#F4F7F5]">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Text Content */}
          <div className="order-2 md:order-1">
            <span className="inline-block bg-[#4A7C59]/10 text-[#4A7C59] font-semibold text-sm px-4 py-2 rounded-full mb-4">
              Semplice da capire
            </span>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] mb-6 leading-tight">
              Cos'è CuraCheck?
            </h2>
            
            <div className="space-y-4 text-lg text-[#5D6D7E]">
              <p>
                <strong className="text-[#2C3E50]">CuraCheck è come un diario digitale</strong> che tiene traccia di tutto quello che fa l'assistente con il tuo familiare.
              </p>
              
              <p>
                Ogni volta che l'assistente completa un'attività, prepara un pasto o segue la routine, <strong className="text-[#4A7C59]">tu ricevi una notifica push istantanea</strong>.
              </p>
              
              <p>
                Così sai sempre come sta il tuo caro, anche se sei lontano.
              </p>
            </div>
            
            {/* Price callout */}
            <div className="mt-8 bg-white rounded-2xl p-6 border-2 border-[#4A7C59]/20 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#FEF3C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <Coffee className="w-7 h-7 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-[#2C3E50] font-bold text-lg">
                    A meno di un caffè al giorno
                  </p>
                  <p className="text-[#64748B] text-sm">
                    Tutta la tranquillità che meriti, a partire da <span className="font-bold text-[#4A7C59]">€6,90/mese</span>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Simple benefits */}
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-[#2C3E50] border border-[#E5E7EB]">
                <CheckCircle2 className="w-4 h-4 text-[#4A7C59]" />
                Niente app da installare
              </span>
              <span className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-[#2C3E50] border border-[#E5E7EB]">
                <CheckCircle2 className="w-4 h-4 text-[#4A7C59]" />
                Notifiche push istantanee
              </span>
              <span className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-[#2C3E50] border border-[#E5E7EB]">
                <CheckCircle2 className="w-4 h-4 text-[#4A7C59]" />
                Facile per tutti
              </span>
            </div>
          </div>
          
          {/* Video YouTube con Thumbnail */}
          <div className="order-1 md:order-2">
            <div className="relative">
              <div className="bg-gradient-to-br from-[#4A7C59]/10 to-[#5DADE2]/10 rounded-3xl p-4">
                <YouTubeThumbnail videoId="AWjPD7AtBfU" />
              </div>
              
              {/* Floating card - Only visible on large screens (lg+) to avoid overlap */}
              <div className="hidden lg:block absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-xl p-4 shadow-xl border border-[#E5E7EB] w-[85%] max-w-[280px]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Nuovo messaggio</p>
                    <p className="text-sm font-semibold text-[#4A7C59]">Maria ha completato la routine ✓</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Notification card below video - Visible on mobile and tablet */}
            <div className="lg:hidden mt-8 bg-white rounded-xl p-4 shadow-lg border border-[#E5E7EB] mx-auto max-w-[320px]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B]">Nuovo messaggio</p>
                  <p className="text-sm font-semibold text-[#4A7C59]">Maria ha completato la routine ✓</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works - WHITE BACKGROUND, compact
const HowItWorksSection = () => {
  const steps = [
    {
      icon: Heart,
      number: "01",
      title: "Crea il profilo",
      description: "Inserisci il nome del tuo familiare e dell'Assistente in pochi secondi.",
    },
    {
      icon: Link2,
      number: "02",
      title: "Invia il link",
      description: "Condividi il link univoco con l'assistente. Non serve installare nulla!",
    },
    {
      icon: Bell,
      number: "03",
      title: "Ricevi aggiornamenti",
      description: "Routine, pasti, umore: tutto in tempo reale sul tuo telefono.",
    }
  ];

  return (
    <section id="come-funziona" className="py-20 px-4 sm:px-6 bg-[#4A7C59] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '50px 50px'
      }} />
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#5DADE2]/10 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#82E0AA]/10 rounded-full blur-[100px]" />
      
      <div className="max-w-6xl mx-auto relative">
        <div className="text-center mb-12">
          <span className="inline-block text-[#82E0AA] font-semibold text-sm uppercase tracking-wider mb-3">
            Semplicissimo
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Come funziona
          </h2>
          <p className="text-white/60 text-lg">
            Tre semplici passi per la tua tranquillità
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <div key={index} className="relative group">
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-white/30 transition-all hover:-translate-y-2 h-full">
                {/* Number Badge */}
                <div className="text-6xl font-black text-white/10 absolute top-4 right-6">
                  {step.number}
                </div>
                
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-[#5DADE2] to-[#82E0AA] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#5DADE2]/20 group-hover:scale-110 transition-transform">
                  <step.icon className="w-8 h-8 text-white" strokeWidth={2} />
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-white/60 leading-relaxed">
                  {step.description}
                </p>
              </div>
              
              {/* Connector Arrow (only between cards) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                  <ChevronRight className="w-6 h-6 text-white/30" />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* CTA to Tutorial */}
        <div className="text-center mt-12">
          <Link 
            to="/tutorial"
            className="inline-flex items-center gap-3 bg-white text-[#1a3a2a] font-bold px-8 py-4 rounded-full hover:bg-white/90 transition-all shadow-xl shadow-black/20 hover:scale-105"
          >
            <span>Come Funziona?</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Phone Mockup Demo - VERDE CHIARO (Sezione 4)
const DemoSection = () => (
  <section className="py-12 px-4 sm:px-6 bg-[#F4F7F5]">
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        {/* Left: Phone Mockup */}
        <div className="relative flex justify-center order-2 lg:order-1">
          <div className="relative">
            {/* Phone frame */}
            <div className="w-[220px] sm:w-[260px] bg-[#1a1a1a] rounded-[36px] p-2 shadow-2xl shadow-black/20">
              {/* Screen */}
              <div className="bg-[#FAF9F6] rounded-[28px] overflow-hidden aspect-[9/19]">
                {/* Status bar */}
                <div className="bg-[#4A7C59] px-4 py-2 flex justify-between items-center">
                  <span className="text-xs text-white/80">9:41</span>
                  <div className="flex gap-1">
                    <div className="w-4 h-2 bg-white/50 rounded-sm" />
                    <div className="w-5 h-2.5 bg-white rounded-sm" />
                  </div>
                </div>
                
                {/* App content */}
                <div className="px-3 py-3 bg-[#FAF9F6]">
                  <div className="text-center mb-3">
                    <p className="text-base font-bold text-[#2C3E50]">Oggi per Maria</p>
                    <p className="text-xs text-[#64748B]">Sabato 15 Marzo</p>
                  </div>
                  
                  {/* Action buttons grid */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="aspect-square rounded-xl bg-[#FCE4EC] flex items-center justify-center text-2xl shadow-sm">💊</div>
                    <div className="aspect-square rounded-xl bg-[#4A7C59] flex items-center justify-center text-2xl shadow-sm">🍽️</div>
                    <div className="aspect-square rounded-xl bg-[#4A7C59] flex items-center justify-center text-2xl shadow-sm">😊</div>
                    <div className="aspect-square rounded-xl bg-[#4A7C59] flex items-center justify-center text-2xl shadow-sm">📝</div>
                  </div>
                  
                  {/* Timeline */}
                  <div className="space-y-2">
                    <div className="bg-white rounded-lg p-2 flex items-center gap-2 shadow-sm">
                      <span className="text-lg">💊</span>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-[#2C3E50]">Routine - Mattina</p>
                        <p className="text-[10px] text-[#64748B]">08:00</p>
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-[#25D366]" />
                    </div>
                    <div className="bg-white rounded-lg p-2 flex items-center gap-2 shadow-sm">
                      <span className="text-lg">🍽️</span>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-[#2C3E50]">Colazione</p>
                        <p className="text-[10px] text-[#64748B]">08:30</p>
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-[#25D366]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating elements */}
            <div className="absolute -top-2 -right-2 bg-white rounded-xl p-2 shadow-lg border border-gray-100">
              <Bell className="w-5 h-5 text-[#4A7C59]" />
            </div>
            <div className="absolute -bottom-2 -left-2 bg-[#25D366] rounded-xl p-2 shadow-lg">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Right: Content */}
        <div className="order-1 lg:order-2 text-center lg:text-left">
          <span className="inline-block text-[#4A7C59] font-semibold text-sm uppercase tracking-wider mb-2">
            L'app dell'Assistente
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-4">
            Semplice da usare, potente nei risultati
          </h2>
          <p className="text-[#64748B] text-base mb-6 leading-relaxed">
            L'Assistente Familiare apre il link e inizia subito a registrare. 
            Un click = una notifica push istantanea.
          </p>
          
          <ul className="space-y-3">
            {[
              { icon: Smartphone, text: "Funziona su qualsiasi telefono" },
              { icon: Clock, text: "Feedback istantaneo" },
              { icon: Camera, text: "Possibilità di allegare foto" }
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="w-10 h-10 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#4A7C59]" />
                </div>
                <span className="text-[#2C3E50] font-medium text-sm">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Trial Registration Modal
const TrialModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nome_utente: '',
    whatsapp_numero: '',
    nome_assistito: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validazione base
    if (!formData.nome_utente.trim() || !formData.whatsapp_numero.trim() || !formData.nome_assistito.trim()) {
      setError('Compila tutti i campi obbligatori');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/register/trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok && data.success) {
        onSuccess(data);
      } else {
        setError(data.detail || 'Errore durante la registrazione');
      }
    } catch (err) {
      setError('Errore di connessione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 animate-in fade-in zoom-in duration-300">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#4A7C59]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-[#4A7C59]" />
          </div>
          <h2 className="text-2xl font-bold text-[#2C3E50]">Prova Gratuita 7 Giorni</h2>
          <p className="text-[#64748B] mt-2">Nessuna carta di credito richiesta</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              <User className="w-4 h-4 inline mr-2" />
              Il tuo nome *
            </label>
            <input
              type="text"
              value={formData.nome_utente}
              onChange={(e) => setFormData({...formData, nome_utente: e.target.value})}
              placeholder="Mario Rossi"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none transition-all"
              data-testid="trial-nome-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              <Phone className="w-4 h-4 inline mr-2" />
              Il tuo numero *
            </label>
            <input
              type="tel"
              value={formData.whatsapp_numero}
              onChange={(e) => setFormData({...formData, whatsapp_numero: e.target.value})}
              placeholder="+39 328 1234567"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none transition-all"
              data-testid="trial-phone-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              <Users className="w-4 h-4 inline mr-2" />
              Nome dell'assistito *
            </label>
            <input
              type="text"
              value={formData.nome_assistito}
              onChange={(e) => setFormData({...formData, nome_assistito: e.target.value})}
              placeholder="Es: Mamma, Papà, Nonna Maria..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none transition-all"
              data-testid="trial-assistito-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              Email (opzionale)
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="mario@email.com"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none transition-all"
            />
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4A7C59] hover:bg-[#3A6345] text-white py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
            data-testid="trial-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Registrazione...
              </>
            ) : (
              <>
                Inizia la Prova Gratuita
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-[#64748B] mt-4">
          Attiva le notifiche push nella dashboard per ricevere aggiornamenti.
          <br />Nessun pagamento richiesto.
        </p>
      </div>
    </div>
  );
};

// Trial Success Modal
const TrialSuccessModal = ({ isOpen, onClose, data }) => {
  const navigate = useNavigate();
  
  if (!isOpen || !data) return null;

  const goToDashboard = () => {
    onClose();
    navigate('/dashboard');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 text-center animate-in fade-in zoom-in duration-300">
        <div className="w-20 h-20 bg-[#4A7C59]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-[#4A7C59]" />
        </div>
        
        <h2 className="text-2xl font-bold text-[#2C3E50] mb-2">Registrazione Completata!</h2>
        <p className="text-[#64748B] mb-6">
          Vai alla Dashboard e attiva le notifiche push per ricevere aggiornamenti in tempo reale.
        </p>
        
        <div className="bg-[#F4F7F5] rounded-xl p-4 mb-6">
          <p className="text-sm text-[#64748B] mb-1">La tua prova gratuita scade il:</p>
          <p className="text-lg font-bold text-[#4A7C59]">
            {new Date(data.trial_end_date).toLocaleDateString('it-IT', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
        
        <Button
          onClick={goToDashboard}
          className="w-full bg-[#4A7C59] hover:bg-[#3A6345] text-white py-3 rounded-xl font-semibold"
        >
          Vai alla Dashboard
        </Button>
      </div>
    </div>
  );
};

// Pricing Section - WHITE BACKGROUND with colored cards
const PricingSection = ({ onSelectPlan, isLoggedIn, onCheckout, user }) => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // Default: Mensile
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState('monthly');
  

  const billingOptions = BILLING_OPTIONS;

  const founder = useFounderSpots();
  const visiblePlans = plansForPeriod(billingPeriod).map((plan) => withFounderStatus(plan, founder));

  const handlePlanClick = async (plan) => {
    const planPeriod = billingPeriod;
    if (plan.isTrial) {
      // Piano Base/Trial - vai a registrazione
      navigate('/registrazione');
    } else if (plan.prices) {
      // Piano a pagamento - se non loggato vai a registrazione, altrimenti checkout
      if (!isLoggedIn) {
        navigate('/registrazione', { state: { plan: plan.id, billingPeriod: planPeriod } });
      } else {
        setSelectedPlan(plan.id);
        setSelectedPlanPeriod(planPeriod);
        setCheckoutModalOpen(true);
      }
    } else {
      onSelectPlan(plan.id, planPeriod);
    }
  };

  return (
    <section id="prezzi" className="py-16 px-4 sm:px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block text-[#4A7C59] font-semibold text-sm uppercase tracking-wider mb-2">
            Prezzi trasparenti
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-4">
            Scegli il piano giusto per te
          </h2>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
            <div
              className="relative inline-flex items-center p-1 rounded-full bg-[#E5E7EB]"
              role="radiogroup"
              data-testid="billing-toggle"
            >
              {billingOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={billingPeriod === option.id}
                  onClick={() => setBillingPeriod(option.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                    billingPeriod === option.id
                      ? 'bg-[#4A7C59] text-white shadow-md'
                      : 'text-[#9CA3AF] hover:text-[#2C3E50]'
                  }`}
                  data-testid={`billing-option-${option.id}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          <p className="text-xs text-[#9CA3AF]">
            IVA inclusa • Disdici quando vuoi
          </p>
          
        </div>

        {/* Banner Prova Gratuita */}
        <div 
          onClick={() => navigate('/registrazione')}
          className="mb-8 cursor-pointer group"
        >
          <div className="bg-gradient-to-r from-[#10B981] to-[#34D399] rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <div className="text-white">
                  <p className="text-sm font-medium opacity-90">Non sei ancora sicuro?</p>
                  <p className="text-xl font-bold">Prova Gratis per 7 Giorni</p>
                  <p className="text-sm opacity-80" data-testid="banner-trial-line">7 giorni gratis, senza carta</p>
                </div>
              </div>
              <Button className="bg-white text-[#10B981] hover:bg-gray-100 px-6 py-3 rounded-xl font-semibold shadow-lg group-hover:shadow-xl transition-all">
                Inizia Gratis
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>

        <PlanCards plans={visiblePlans} period={billingPeriod} onSelect={handlePlanClick} showPaymentMethods />
        
        {/* Premium Feature Note - Gestione Ricette */}
        <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200 max-w-2xl mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-[#2C3E50] mb-1">Gestione Ricette Premium</p>
              <p className="text-sm text-[#64748B]">
                Configura il tuo medico <strong>una volta sola</strong>. Richiedi i farmaci con un clic, 
                pensiamo noi a inviare Email allo studio medico.
              </p>
            </div>
          </div>
        </div>
        
        {/* Garanzia Legale Completa */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#64748B] max-w-2xl mx-auto leading-relaxed">
            <Shield className="w-4 h-4 inline mr-1 text-[#4A7C59]" />
            <strong>Prova CuraCheck senza pensieri.</strong> Se non sei soddisfatto entro i primi 14 giorni, 
            ti rimborsiamo l'intera quota senza domande. Ai sensi del Codice del Consumo, la tua serenità è tutelata.
          </p>
        </div>
      </div>
      
      {/* Checkout Modal */}
      <CheckoutModal 
        isOpen={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        plan={selectedPlan}
        billingPeriod={selectedPlanPeriod}
        user={user}
        onSoldOut={founder.markSoldOut}
      />
    </section>
  );
};

// Final CTA Section - GREEN GRADIENT
const FinalCtaSection = ({ onCtaClick }) => (
  <section className="py-16 px-4 sm:px-6 bg-gradient-to-br from-[#4A7C59] to-[#3A6345]">
    <div className="max-w-3xl mx-auto text-center">
      {/* Trial Badge */}
      <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-white/30">
        <Clock className="w-4 h-4" />
        <span>7 giorni di prova gratuita</span>
      </div>
      
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
        Pronto a ritrovare la serenità?
      </h2>
      <p className="text-white/80 text-base mb-6 max-w-xl mx-auto">
        Inizia oggi con CuraCheck. Setup in 2 minuti.
      </p>
      <div className="flex flex-col items-center gap-3">
        <Button
          onClick={onCtaClick}
          className="bg-white text-[#4A7C59] hover:bg-gray-100 px-8 py-4 rounded-full font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105"
        >
          Inizia la Prova Gratuita
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <span className="text-white/60 text-xs font-medium" data-testid="final-cta-trial-line">
          7 giorni gratis, senza carta • Cancella quando vuoi
        </span>
      </div>
    </div>
  </section>
);

// FAQ Section - Accordion Style
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-[#4A7C59]/20 last:border-b-0">
    <button
      className="w-full py-5 px-1 flex items-center justify-between text-left group"
      onClick={onClick}
    >
      <span className="text-lg font-semibold text-[#2C3E50] group-hover:text-[#4A7C59] transition-colors pr-4">
        {question}
      </span>
      <ChevronDown 
        className={`w-5 h-5 text-[#4A7C59] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
      />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
      <p className="text-[#64748B] leading-relaxed px-1">
        {answer}
      </p>
    </div>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Devo installare un'app sul telefono dell'assistente?",
      answer: "No. È questo il punto di forza di CuraCheck. L'assistente riceve un semplice link. Cliccandoci, si apre una schermata facilissima con i tasti per confermare le attività (pasto, terapia, ecc.). Non deve scaricare nulla, né registrarsi."
    },
    {
      question: "Su quale numero riceverò le notifiche?",
      answer: "Le riceverai come notifiche push direttamente sul tuo telefono, dopo aver attivato le notifiche nella dashboard. Sarai tu ad avere il controllo totale e a ricevere gli aggiornamenti in tempo reale, ovunque tu sia."
    },
    {
      question: "Cosa succede se l'assistente dimentica di cliccare sul link?",
      answer: "CuraCheck è progettato per ricordarglielo. Se l'attività non viene confermata negli orari stabiliti, il sistema può inviare un promemoria automatico all'assistente, assicurandoti che la routine venga rispettata."
    },
    {
      question: "Posso aggiungere altri familiari (fratelli/sorelle) alle notifiche?",
      answer: "Certamente. Con il piano Premium puoi inviare notifiche a 2 destinatari, più Team e Calendario e il registro delle passeggiate (inizio, fine e durata)."
    },
    {
      question: "I dati e i report sono sicuri?",
      answer: "La privacy è la nostra priorità. I dati sugli orari e le attività sono criptati e accessibili solo a te. Il report settimanale è un documento privato pensato per aiutarti a monitorare la routine dei tuoi cari e da condividere, se vuoi, con chi ritieni opportuno."
    },
    {
      question: "Posso disdire l'abbonamento quando voglio?",
      answer: "Sì, senza vincoli. Puoi annullare il tuo abbonamento in qualsiasi momento con un semplice clic dalla tua area personale. Non ci sono costi di disattivazione o penali."
    },
    {
      question: "Come funziona la garanzia di rimborso?",
      answer: "Se non sei soddisfatto di CuraCheck entro i primi 14 giorni dall'acquisto, ti rimborsiamo l'intera quota senza domande. È un tuo diritto ai sensi del Codice del Consumo. Per richiedere il rimborso, scrivi semplicemente a assistenzaclienti@cura-check.com indicando il tuo nome e la data di acquisto. Riceverai il rimborso entro 7 giorni lavorativi."
    },
    {
      question: "Come posso eliminare il mio account e tutti i miei dati?",
      answer: "Rispettiamo pienamente i tuoi diritti GDPR. Dalla tua Dashboard, vai su Impostazioni → Privacy e Dati Personali. Lì puoi: (1) Esportare tutti i tuoi dati in formato JSON (Diritto alla portabilità), (2) Eliminare definitivamente il tuo account e tutti i dati associati (Diritto all'oblio). La cancellazione è immediata e irreversibile. Per assistenza, scrivi a privacy@cura-check.com."
    }
  ];

  return (
    <section id="faq" className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-[#F0F7F2]">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#4A7C59]/10 mb-4">
            <HelpCircle className="w-7 h-7 text-[#4A7C59]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] mb-4">
            Domande Frequenti
          </h2>
          <p className="text-[#64748B] text-lg">
            Tutto quello che devi sapere su CuraCheck
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="bg-white rounded-2xl shadow-lg shadow-[#4A7C59]/5 border border-[#E5E7EB] p-6 sm:p-8">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <p className="text-[#64748B] mb-4">
            Hai altre domande? Scrivici!
          </p>
          <a 
            href="mailto:info@cura-check.com" 
            className="inline-flex items-center gap-2 text-[#4A7C59] font-semibold hover:underline"
          >
            <MessageCircle className="w-5 h-5" />
            info@cura-check.com
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer - DARK
const Footer = () => (
  <footer className="py-10 px-4 sm:px-6 bg-[#2C3E50]">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="flex items-center gap-3">
          <Logo className="h-20 sm:h-24" darkBg={true} />
        </div>
        
        <div className="flex flex-wrap justify-center gap-6 text-white/60 text-sm">
          <Link 
            to="/blog" 
            className="hover:text-white transition-colors font-medium"
          >
            Blog
          </Link>
          <Link 
            to="/privacy-policy" 
            className="hover:text-white transition-colors"
          >
            Privacy Policy
          </Link>
          <Link 
            to="/cookie-policy" 
            className="hover:text-white transition-colors"
          >
            Cookie Policy
          </Link>
          <CookiePreferencesLink className="hover:text-white transition-colors" testId="footer-cookie-preferences" />
          <Link 
            to="/termini-e-condizioni" 
            className="hover:text-white transition-colors"
          >
            Termini e Condizioni
          </Link>
          <Link 
            to="/cancella-iscrizione" 
            className="hover:text-white transition-colors"
          >
            Cancella Iscrizione
          </Link>
          <a href="mailto:info@cura-check.com" className="hover:text-white transition-colors">Contatti</a>
        </div>
      </div>
      
      {/* Legal Company Info */}
      <div className="mt-6 pt-6 border-t border-white/10 text-center space-y-2">
        <p className="text-white/50 text-sm font-medium">
          A.M.D. GENERAL HOLDING S.R.L.S.
        </p>
        <p className="text-white/40 text-xs">
          P.IVA 15979301007 | Sede Legale: Via Domenico Orano 8, 00156 Roma
        </p>
        <p className="text-white/50 text-xs">
          CuraCheck® è un marchio di A.M.D. GENERAL HOLDING S.R.L.S.
        </p>
        <p className="text-white/40 text-sm mt-3">
          © 2026 CuraCheck - Tutti i diritti riservati
        </p>
      </div>
      
      {/* Services disclosure */}
      <div className="mt-4 pt-4 border-t border-white/10 text-center">
        <p className="text-white/30 text-xs">
          CuraCheck utilizza Stripe per i pagamenti sicuri e Firebase per le notifiche push.
        </p>
      </div>
      
      {/* Platform Disclaimer */}
      <div className="mt-4 text-center">
        <p className="text-white/40 text-xs leading-relaxed max-w-2xl mx-auto">
          CuraCheck è una piattaforma tecnologica di logistica familiare (SaaS). 
          Non fornisce servizi sanitari, diagnosi o consulenze mediche.
        </p>
      </div>
    </div>
  </footer>
);

// Main Landing Page Component
const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  // Arrivo da "Abbonamento" senza abbonamento Stripe: mostra i prezzi anche se loggato
  const showPrezzi = location.state?.section === 'prezzi';

  useEffect(() => {
    if (!loading && showPrezzi) {
      setTimeout(() => document.getElementById('prezzi')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [loading, showPrezzi]);

  // MIDDLEWARE: Deterministic redirect — family session first, then caregiver token
  useEffect(() => {
    // 1. Wait for auth check to complete
    if (loading) return;
    
    // 2. If logged-in user (family), redirect to dashboard
    if (user && !showPrezzi) {
      console.log('User already logged in, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // 3. If caregiver token exists, redirect to caregiver app
    const assistantToken = localStorage.getItem('curacheck_assistant_token');
    if (assistantToken) {
      console.log('Badante già associata, redirect all\'app assistente...');
      navigate(`/app/${assistantToken}`, { replace: true });
    }
  }, [user, loading, navigate, showPrezzi]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSelectPlan = (plan, billingPeriod = 'monthly') => {
    navigate('/registrazione', { state: { plan, billingPeriod } });
  };

  // Mostra loading mentre verifichiamo l'auth
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#4A7C59] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#64748B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Se utente loggato, non mostrare nulla (il redirect è in corso)
  if (user && !showPrezzi) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <HeroSection onCtaClick={() => navigate('/registrazione')} />
      <WhatIsCuraCheckSection />
      <HowItWorksSection />
      <TestimonialsSection />
      <DemoSection />
      <PricingSection onSelectPlan={handleSelectPlan} isLoggedIn={!!user} />
      <FAQSection />
      <FinalCtaSection onCtaClick={() => navigate('/registrazione')} />
      <Footer />
    </div>
  );
};

export default LandingPage;
